<template>
    <div :class="{ 'margin-bottom-35': isLeftOrRight }" id="userInputMain">
        <input type="file" v-show="false" @change="fileChange" id="fileInput" />
        <div ref="waitAlert" class="wait-alert" v-if="!visitorWriteEmptyChat">
            <p>{{ $t("message.pleaseWaitConnectUser") }}</p>
        </div>
        <div
            v-if="file"
            :style="{
                backgroundColor: colors.userInput.text,
            }"
            style="color: var(--color4)"
            class="file-container"
        >
            <span class="icon-file-message"><img :alt="icons.file.name" :src="icons.file.img" height="15" /></span>
            {{ file.name }}
            <span class="delete-file-message" @click="cancelFile()"
                ><img :alt="icons.closeSvg.name" :src="icons.closeSvg.img" height="10" title="Remove the file"
            /></span>
        </div>
        <div class="userInputWrapper">
            <textarea
                v-if="!voiceRecorder.isRecording"
                ref="userInput"
                v-model="text"
                style="color: #0c0c0c !important"
                :readonly="prechatLocked"
                :placeholder="activePlaceholder"
                class="userInputText"
                :class="{ 'is-locked': prechatLocked }"
                @mousedown="onLockedTap"
                rows="1"
                autocomplete="off"
                @blur="setInputActive(false)"
                @focus="setInputActive(true)"
                @focusUserInput="focusUserInput()"
                @input="autoGrow"
                @change="autoGrow"
                @keydown="handleKey"
                @keyup="handleKeyUp"
            ></textarea>
            <div style="display: flex">
                <div
                    v-if="!voiceRecorder.isRecording && !prechat.enabled"
                    class="emoji-icon-wrapper"
                    @click="emojiClickHandler"
                >
                    <EmojiIcon color="var(--button-color)" style="cursor: pointer" />
                </div>
                <div
                    class="userInputKeyboard"
                    v-if="visitorWriteEmptyChat || isAnyUserConnected || !connected || chatbotActive"
                >
                    <div @keydown.esc="showEmojiTable = false" v-if="showEmojiTable" class="emoji-table-container">
                        <EmojiPicker :onEmojiPicked="_handleEmojiPicked" @value="showEmojiTable = $event" />
                    </div>

                    <div
                        v-if="
                            !voiceRecorder.isRecording &&
                            (connected || previewMode) &&
                            (visitorFileSendEnabled || visitorLocationEnabled)
                        "
                        class="select-media-button"
                        @click="showList = true"
                    >
                        <LocationShare
                            :isLocationClick="isLocationClicked"
                            @onLocationClicked="isLocationClicked = $event"
                        />
                        <MediaPopup
                            v-if="showList && (visitorFileSendEnabled || visitorLocationEnabled)"
                            @openLocation="isLocationClicked = true"
                            @closeMediaPopup="showList = false"
                        />
                        <FileIcon color="var(--button-color)" style="cursor: pointer" />
                    </div>

                    <user-input-button
                        v-if="isEditing"
                        :color="colors.userInput.text"
                        tooltip="cancel"
                        @click.native.prevent="_editFinish"
                    >
                        <icon-cross />
                    </user-input-button>

                    <user-input-button
                        v-if="isEditing"
                        color="var(--color4)"
                        tooltip="Edit"
                        @click.native.prevent="_editText"
                    >
                        <icon-ok />
                    </user-input-button>
                    <div>
                        <md-button
                            v-show="!visitorVoiceRecordEnabled || (!voiceRecorder.isRecording && text != '')"
                            class="md-icon-button md-dense send-icon"
                            :class="{
                                active: !visitorVoiceRecordEnabled || (!voiceRecorder.isRecording && text != ''),
                            }"
                            @click="_submitText"
                            @mousedown="(e) => e.preventDefault()"
                        >
                            <md-icon style="color: var(--button-color)">send</md-icon>
                        </md-button>
                    </div>

                    <md-button
                        v-show="!voiceRecorder.isRecording && text == '' && (connected || previewMode) && visitorVoiceRecordEnabled"
                        class="actionBtn md-icon-button md-dense send-icon"
                        :class="{
                            active: !voiceRecorder.isRecording && text == '' && (connected || previewMode) && visitorVoiceRecordEnabled,
                        }"
                        @click="setRecording(true)"
                    >
                        <md-icon>mic</md-icon>
                    </md-button>
                </div>
            </div>
            <VoiceRecorder v-show="text == '' && $store.state.voiceRecorder.isRecording" :cb="sendVoiceMessage" />
        </div>
    </div>
</template>

<script>
import UserInputButton from "./UserInputButton.vue";
import EmojiPicker from "./EmojiPicker";
import CloseIconSvg from "./assets/close.svg";
import store from "./store/";
import IconCross from "./components/icons/IconCross.vue";
import IconOk from "./components/icons/IconOk.vue";
import IconSend from "./components/icons/IconSend.vue";
import { log2 } from "../../../utils/log2";
import { uploadAsset } from "../../../api/assetUpload";
import VoiceRecorder from "../VoiceRecorder";
import { uzantiOf } from "../../util/voiceRecorder";
import { mapGetters, mapMutations, mapState } from "vuex";
import axios from "axios";
import { sendSocketMessage } from "../../../utils/socketStream";
import mimeTypes from "../../../utils/mimeTypes";
import { parentWindowMessageStream } from "../../../utils/parentWindowConnection";
import { filter } from "rxjs/operators";
import _ from "lodash";
import LocationShare from "../LocationShare.vue";
import EllipsIconButton from "../../../common/IconButton.vue";
import DynamicSvg from "../../../common/DynamicSvg.vue";
import MediaPopup from "./MediaPopup.vue";
import StringUtils from "../../util/StringUtils";
import EmojiIcon from "../svg/components/EmojiIcon.vue";
import FileIcon from "../svg/components/FileIcon.vue";

const log = log2("UserInput");
export default {
    components: {
        MediaPopup,
        LocationShare,
        EmojiPicker,
        UserInputButton,
        IconCross,
        VoiceRecorder,
        IconOk,
        IconSend,
        EllipsIconButton,
        DynamicSvg,
        EmojiIcon,
        FileIcon,
    },
    props: {
        sendTyping: {
            type: Function,
            default: () => {},
        },
        icons: {
            type: Object,
            required: false,
            default: function () {
                return {
                    file: {
                        img: FileIcon,
                        name: "default",
                    },
                    closeSvg: {
                        img: CloseIconSvg,
                        name: "default",
                    },
                };
            },
        },
        showEmoji: {
            type: Boolean,
            default: () => false,
        },
        showFile: {
            type: Boolean,
            default: () => false,
        },
        onSubmit: {
            type: Function,
            required: true,
        },
        // Karsilama diyalogu sirasinda cubugun sahibi WelcomeDialog olur:
        // yer tutucu o anki soru, secim adimlarinda kutu kilitli.
        prechat: {
            type: Object,
            default: () => ({ enabled: false, locked: false, placeholder: "", prefill: "" }),
        },
        placeholder: {
            type: String,
            default: "Write something...",
        },
        colors: {
            type: Object,
            required: true,
        },
        chatbotActive: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            text: "",
            file: null,
            inputActive: false,
            store,
            showList: false,
            showEmojiTable: false,
            isLocationClicked: false,
            isEmojisClicked: false,
            sendIconClass: true,
        };
    },
    methods: {
        ...mapMutations({
            setRecording: "voiceRecorder/SET_RECORDING",
        }),
        emojiClickHandler() {
            this.showEmojiTable = true;
        },
        async sendVoiceMessage(blob) {
            let sideId = this.site._id;
            let chatId = this.chat.conversationId;
            let path = `${sideId}/voice/${chatId}`;
            // Kayit her tarayicida mp3 degil: bicim kaydin kendisinden okunuyor.
            // Sabit "audio/mpeg" + "mp3" yuzunden dosyalar acilmiyordu.
            const mimeType = blob.type || "audio/webm";
            const ext = uzantiOf(mimeType);
            let { data } = await uploadAsset({
                path,
                mimeType,
                mime: mimeType,
                ext,
                size: blob.size,
                type: "conversation",
                platform: "web",
            });

            await axios({
                method: data.method,
                url: data.url,
                data: blob,
                headers: { "Content-Type": mimeType },
            });

            sendSocketMessage("visitor media message", {
                conversationId: this.conversationId,
                visitorId: this.visitorId,
                siteId: this.site._id,
                asset: data.asset,
                chatBotEnable: this.isChatbotActive,
            });
            log("sendVoiceMessage", { blob, data });
        },
        cancelFile() {
            this.file = null;
        },
        handleLocationClick() {
            this.showList = false;
            this.isLocationClicked = true;
        },
        setInputActive(onoff) {
            this.inputActive = onoff;
        },
        // Kilitliyken cubuk yalnizca formu isaret ediyor: mesaji yutup
        // ust bilesene haber veriyoruz, o da formu titretiyor.
        onLockedTap(event) {
            if (!this.prechatLocked) return;
            event.preventDefault();
            this.$emit("locked-attempt");
        },
        _submitText(event) {
            if (this.prechatLocked) {
                this.$emit("locked-attempt");
                return;
            }
            const file = this.file;
            if (file) {
                this._submitTextWhenFile(event, this.text, file);
            } else {
                if (this.text && this.text.length > 0) {
                    if (
                        this.prechat.enabled ||
                        this.visitorWriteEmptyChat ||
                        this.isAnyUserConnected ||
                        !this.connected ||
                        this.chatbotActive
                    ) {
                        this.onSubmit(this.text);
                        this.text = "";
                        this.$refs.userInput.value = "";
                        this.resetGrow();
                    }
                }
            }
        },
        handleKey(event) {
            log.debug("handleKey", event);
            if (event.keyCode === 13 && !event.shiftKey) {
                if (!this.isEditing) {
                    this.$emit("pressEnter");
                    this._submitText(event);
                } else {
                    this._editText(event);
                }
                this._editFinish();
                event.preventDefault();
            } else if (event.keyCode === 27) {
                this._editFinish();
                event.preventDefault();
            }
            if (this.$refs.userInput) {
                const text = this.$refs.userInput.value;
                //some of the android devices text state not fired with v-model
                setTimeout(() => {
                    if (this.text !== this.$refs.userInput.value) {
                        this.text = this.$refs.userInput.value;
                    }
                }, 0);

                // this.sendTyping(text);
            }
        },
        handleKeyUp(event) {
            log.debug("handleKeyUp", event);

            if (this.$refs.userInput) {
                const text = this.$refs.userInput.value;
                this.sendTyping(text);
            }
        },

        focusUserInput() {
            if (this.$nextTick) {
                this.$nextTick(() => {
                    if (this.$refs.userInput) this.$refs.userInput.focus();
                });
            }
        },
        _submitTextWhenFile(event, text, file) {
            if (text && text.length > 0) {
                this.onSubmit({
                    author: "me",
                    type: "file",
                    data: { text, file },
                });
                this.file = null;
                this.$refs.userInput.innerHTML = "";
            } else {
                this.onSubmit({
                    author: "me",
                    type: "file",
                    data: { file },
                });
                this.file = null;
            }
        },
        _editText(event) {
            const text = this.$refs.userInput.textContent;
            if (text && text.length) {
                this.$emit("edit", {
                    author: "me",
                    type: "text",
                    id: store.editMessage.id,
                    data: { text },
                });
                this._editFinish();
            }
        },
        _handleEmojiPicked(emoji) {
            this.text += emoji;
            this.$refs.userInput.focus();
            this.$nextTick(this.autoGrow);
        },
        // Yazdikca kutuyu buyutur; CSS'teki max-height'e gelince kendi icinde kaydirir.
        autoGrow() {
            const el = this.$refs.userInput;
            if (!el) return;
            el.style.height = "auto";
            el.style.height = el.scrollHeight + "px";
        },
        // Mesaj gidince kutu tek satira doner.
        resetGrow() {
            const el = this.$refs.userInput;
            if (!el) return;
            el.style.height = "auto";
        },
        _handleFileSubmit(file) {
            this.file = file;
        },
        _editFinish() {
            this.store.editMessage = null;
        },
        cleanFileName(fileName) {
            const lastDotIndex = fileName.lastIndexOf('.');
            const name = lastDotIndex !== -1 ? fileName.substring(0, lastDotIndex) : fileName;
            const extension = lastDotIndex !== -1 ? fileName.substring(lastDotIndex) : '';

            const cleanedName = name
                .replace(/[çÇ]/g, 'c')
                .replace(/[ğĞ]/g, 'g')
                .replace(/[ıİ]/g, 'i')
                .replace(/[öÖ]/g, 'o')
                .replace(/[şŞ]/g, 's')
                .replace(/[üÜ]/g, 'u')
                .replace(/[^a-zA-Z0-9._-]/g, '_')
                .replace(/_+/g, '_')
                .replace(/^_|_$/g, '');

            return cleanedName + extension;
        },
        async fileChange({ target: fileEl }) {
            log.debug("fileChange", fileEl.files);
            this.showList = false;
            let file = fileEl.files[0];
            let path = `${this.site._id}/file/${this.conversationId}`;
            const fileType = StringUtils.getExtensionFromFileName(file.name);
            const cleanedFileName = this.cleanFileName(file.name);
            let { data } = await uploadAsset({
                path,
                mime: file.type,
                mimeType: file.type,
                ext: mimeTypes[file.type] || fileType || null,
                name: cleanedFileName,
                size: file.size,
                platform: "web",
                type: "conversation",
            });
            log("file", { file, data });
            if (file.size <= 10 * 1024 * 1024) {
                await axios({
                    method: data.method,
                    url: data.url,
                    data: file,
                    headers: {
                        "Content-Type": file.type,
                    },
                });
                sendSocketMessage("visitor media message", {
                    conversationId: this.conversationId,
                    visitorId: this.visitorId,
                    siteId: this.site._id,
                    asset: data.asset,
                    chatBotEnable: this.isChatbotActive,
                });
            } else {
                log.warn("input is not suitable for send");
            }
            fileEl.value = "";
        },
    },
    watch: {
        // Donen ziyaretcinin bilgisi cubuga onden yaziliyor; gondersin ya da duzeltsin.
        "prechat.prefill": {
            handler(value) {
                if (!this.prechat.enabled) return;
                this.text = value || "";
                this.$nextTick(() => (this.text ? this.autoGrow() : this.resetGrow()));
            },
            immediate: true,
        },
        editMessageId(m) {
            // textContent yaziliyordu; input'ta hicbir sey yapmiyordu, textarea'da
            // v-model ile ayrisiyor. Metni dogrudan modele yaz.
            if (store.editMessage != null && store.editMessage != undefined) {
                this.$refs.userInput.focus();
                this.text = store.editMessage.data.text;
                this.$nextTick(this.autoGrow);
            } else {
                this.text = "";
                this.resetGrow();
                this.focusUserInput();
            }
        },
        connectingRepresentative() {
            const el = this.$refs.waitAlert;
            if (el) {
                if (!this.chatbotActive && this.connectingRepresentative) {
                    el.style.display = "flex";
                    el.style.transform = "translateY(0)";
                } else {
                    el.style.transform = "translateX(100%)";
                    setTimeout(() => {
                        el.style.display = "none";
                    }, 300);
                }
            }
        },
    },
    computed: {
        activePlaceholder() {
            if (!this.prechat.enabled) return this.placeholder;
            return this.prechat.placeholder || this.placeholder;
        },
        prechatLocked() {
            return !!(this.prechat.enabled && this.prechat.locked);
        },
        ...mapState({
            site: (state) => state.site.site,
            connected: (state) => state.chat.connected,
            conversationId: (state) => state.chat.conversationId,
            visitorId: (state) => state.chat.visitorId,
            chat: (state) => state.chat,
            settings: (state) => state.setting.settings,
            showAfterActions: (state) => state.app.showAfterActions,
            visitorFileSendEnabled: (state) => _.get(state, "setting.settings.visitorFileSendEnabled", true),
            // panel onizlemesi bagli sohbet degil; giris cubugu yine de ayarlarin gercek halini gostersin
            previewMode: (state) => state.app.previewMode,
            visitorVoiceRecordEnabled: (state) => _.get(state, "setting.settings.visitorVoiceRecordEnabled", true),
            visitorLocationEnabled: (state) => _.get(state, "setting.settings.visitorLocationEnabled", true),
            visitorWriteEmptyChat: (state) => _.get(state, "setting.settings.visitorWriteEmptyChat", true),
            voiceRecorder: (state) => state.voiceRecorder,
            chatAgents: (state) => state.chat.chatAgents,
            isChatbotActive: (state) => state.chat.isChatbotActive,
        }),
        ...mapGetters({
            connectingRepresentative: "chat/connectingRepresentative",
        }),
        editMessageId() {
            return this.isEditing && store.editMessage.id;
        },
        isEditing() {
            return store.editMessage && store.editMessage.id;
        },
        isAnyUserConnected() {
            return !!this.chatAgents.length;
        },
        isLeftOrRight() {
            return (
                this.settings.chatCustomizationSettings.position === "left" ||
                this.settings.chatCustomizationSettings.position === "right"
            );
        },
    },
    mounted() {
        const el = this.$refs.waitAlert;
        if (el) {
            if (!this.chatbotActive && this.connectingRepresentative) {
                el.style.display = "flex";
                el.style.transform = "translateY(0)";
            } else {
                el.style.transform = "translateX(100%)";
                setTimeout(() => {
                    if (el) {
                        el.style.display = "none";
                    }
                }, 300);
            }
        }
        this.$root.$on("focusUserInput", () => {
            this.focusUserInput();
        });
        this.focusSub = parentWindowMessageStream
            .pipe(filter((x) => ["iframe-open", "maximize"].includes(x.command)))
            .subscribe(() => {
                this.focusUserInput();
            });
        this.focusUserInput();
    },
    beforeDestroy() {
        if (this.focusSub) this.focusSub.unsubscribe();
    },
};
</script>

<style lang="scss" scoped src="../../../assets/scss/components/v2/UserInput.scss"></style>
<style scoped>
.margin-bottom-35 {
    margin-bottom: 35px;
}
</style>
<style lang="scss">
#userInputMain {
    // Kartin dibine yasla; fixed iken iframe'in dibine yaslanip kartin
    // altindaki 40px'lik seride tasiyordu.
    position: absolute !important;
    bottom: 0 !important;
    // Kart 23px yuvarlak ve overflow: visible; kose vermezsek cubugun beyaz
    // zemini alt kosenin uzerine tasip karti kare gosteriyor.
    border-bottom-left-radius: var(--sps-widget-radius, 20px) !important;
    border-bottom-right-radius: var(--sps-widget-radius, 20px) !important;
    left: 0 !important;
    right: 0 !important;
    margin-bottom: 0 !important;
    padding: 10px var(--sps-gutter, 12px) !important;
    padding-bottom: max(10px, env(safe-area-inset-bottom)) !important;
    width: 100% !important;
    box-sizing: border-box !important;
}
</style>



// WEBPACK FOOTER //
// src/components/V2/popup-chat/UserInput.vue